import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileText, Check, X, Ban, ChevronDown, ChevronUp, Clock } from "lucide-react";
import type { Quote } from "@/hooks/useQuotes";
import { isQuoteExpired } from "@/hooks/useQuotes";

interface DevisCardProps {
  quote: Quote | null | undefined;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onCancel: (id: string) => void;
  onView: (id: string) => void;
  isArtisan: boolean;
  loading?: boolean;
}

const DELAY_LABELS: Record<string, string> = {
  within_24h: "Sous 24h",
  within_3_days: "Sous 3 jours",
  within_1_week: "Sous 1 semaine",
  flexible: "À convenir",
};

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  sent:      { label: "En attente", variant: "secondary" },
  viewed:    { label: "Vu",         variant: "secondary" },
  accepted:  { label: "Accepté",    variant: "default" },
  rejected:  { label: "Refusé",     variant: "destructive" },
  expired:   { label: "Expiré",     variant: "outline" },
  cancelled: { label: "Annulé",     variant: "outline" },
};

const DevisCard = ({ quote, onAccept, onReject, onCancel, onView, isArtisan, loading }: DevisCardProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [viewed, setViewed] = useState(false);

  if (!quote) return null;

  const expired = isQuoteExpired(quote);
  // Statut d'affichage : si le statut DB est sent/viewed mais que la date est passée, afficher "Expiré"
  const displayStatus = (quote.status === "sent" || quote.status === "viewed") && expired
    ? "expired"
    : quote.status;
  const config = statusConfig[displayStatus] || statusConfig.sent;

  const isActive = quote.status === "sent" || quote.status === "viewed";
  const canClientAct = !isArtisan && isActive && !expired;
  const canArtisanAct = isArtisan && isActive && !expired;

  const handleCardClick = () => {
    if (!isArtisan && !viewed && (quote.status === "sent")) {
      setViewed(true);
      onView(quote.id);
    }
  };

  const validUntilDate = new Date(quote.valid_until);

  return (
    <>
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler ce devis ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le devis « {quote.title} » sera annulé. Le client en sera informé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Garder</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { onCancel(quote.id); setShowCancelConfirm(false); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Annuler le devis
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showRejectConfirm} onOpenChange={setShowRejectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refuser ce devis ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous refuserez le devis « {quote.title} ». L'artisan en sera informé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { onReject(quote.id); setShowRejectConfirm(false); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Refuser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className="border rounded-xl p-3 bg-card shadow-sm max-w-xs mx-auto cursor-default"
        onClick={handleCardClick}
        role={!isArtisan && quote.status === "sent" ? "button" : undefined}
        tabIndex={!isArtisan && quote.status === "sent" ? 0 : undefined}
        onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
        aria-label={`Devis : ${quote.title}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">
            DEVIS-{quote.id.slice(0, 4).toUpperCase()}
          </span>
          <Badge variant={config.variant} className="text-[10px]">
            {config.label}
          </Badge>
        </div>

        {/* Titre */}
        <h4 className="font-display font-semibold text-sm mb-1.5 flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
          {quote.title}
        </h4>

        {/* Description tronquée */}
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{quote.description}</p>

        {/* Montant + validité */}
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-bold text-base text-primary">
            {quote.total_amount.toLocaleString("fr-DZ")} DZD
          </span>
          <span className={`flex items-center gap-1 ${expired && isActive ? "text-destructive" : "text-muted-foreground"}`}>
            <Clock className="h-3 w-3" />
            {expired && isActive
              ? "Expiré"
              : `Valide jusqu'au ${validUntilDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`
            }
          </span>
        </div>

        {/* Délai d'intervention */}
        {quote.estimated_intervention_delay && (
          <p className="text-xs text-muted-foreground mb-2">
            Intervention : {DELAY_LABELS[quote.estimated_intervention_delay] ?? quote.estimated_intervention_delay}
          </p>
        )}

        {/* Lignes de détail (collapsibles) */}
        {quote.line_items && quote.line_items.length > 0 && (
          <div className="border-t mt-2 pt-2">
            <button
              type="button"
              className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }}
            >
              <span>{quote.line_items.length} prestation{quote.line_items.length > 1 ? "s" : ""}</span>
              {showDetails ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showDetails && (
              <div className="mt-2 space-y-1">
                {quote.line_items.map((item, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{item.label} × {item.quantity}</span>
                    <span className="font-medium">{item.total_price.toLocaleString("fr-DZ")} DZD</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Date acceptation */}
        {quote.status === "accepted" && quote.accepted_at && (
          <p className="text-xs text-accent font-medium mt-2">
            ✅ Accepté le {new Date(quote.accepted_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
          </p>
        )}

        {/* Actions */}
        <div className="mt-3 space-y-2">
          {canClientAct && (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="default"
                className="flex-1 text-xs h-8 bg-accent hover:bg-accent/90"
                onClick={(e) => { e.stopPropagation(); onAccept(quote.id); }}
                disabled={loading}
              >
                <Check className="h-3 w-3 me-1" /> Accepter
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs h-8"
                onClick={(e) => { e.stopPropagation(); setShowRejectConfirm(true); }}
                disabled={loading}
              >
                <X className="h-3 w-3 me-1" /> Refuser
              </Button>
            </div>
          )}
          {canArtisanAct && (
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-xs h-8 text-destructive hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); setShowCancelConfirm(true); }}
              disabled={loading}
            >
              <Ban className="h-3 w-3 me-1" /> Annuler le devis
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default DevisCard;
